/**
 * Push Notifications Service using OneSignal + Despia Native
 * Uses external_id approach for targeting users
 * 
 * IMPORTANT: Follows Despia SDK registration protocol:
 * 1. requestpushpermission:// - Request iOS permission popup
 * 2. registerpush:// - Register APNs token with OneSignal
 * 3. setonesignalplayerid:// - Link external_id to subscription
 */

import { supabase } from "@/integrations/supabase/client";
import despia from "despia-native";

const ONESIGNAL_APP_ID = "c24e2286-9493-48f1-a43a-670d9b847f09";

// Mutex to prevent concurrent registration attempts
let isLinkingInProgress = false;
let lastLinkAttempt = 0;
const LINK_DEBOUNCE_MS = 5000;

// Diagnostics state for UI
export interface PushDiagnostics {
  userAgent: string;
  isDespia: boolean;
  hasOneSignalPlugin: boolean;
  platform: string;
  registrationSteps: Array<{
    step: string;
    timestamp: Date;
    success: boolean;
    error?: string;
  }>;
  lastUserId?: string;
}

let diagnosticsState: PushDiagnostics = {
  userAgent: '',
  isDespia: false,
  hasOneSignalPlugin: false,
  platform: '',
  registrationSteps: [],
};

export function getPushDiagnostics(): PushDiagnostics {
  return { ...diagnosticsState };
}

function addDiagnosticStep(step: string, success: boolean, error?: string) {
  diagnosticsState.registrationSteps.push({
    step,
    timestamp: new Date(),
    success,
    error,
  });
  // Keep only last 20 steps
  if (diagnosticsState.registrationSteps.length > 20) {
    diagnosticsState.registrationSteps = diagnosticsState.registrationSteps.slice(-20);
  }
  console.log(`[Push] ${step}: ${success ? '✓' : '✗'}${error ? ` (${error})` : ''}`);
}

interface OneSignalWindow extends Window {
  OneSignal?: {
    init(options: { appId: string;[key: string]: any }): Promise<void>;
    login(externalId: string): Promise<void>;
    logout(): Promise<void>;
    User: {
      PushSubscription: {
        id: string | null;
        token: string | null;
        optedIn: boolean;
        addEventListener(event: string, callback: (data: unknown) => void): void;
      };
      addTag(key: string, value: string): void;
      addTags(tags: Record<string, string>): void;
      removeTag(key: string): void;
    };
    Notifications: {
      requestPermission(): Promise<boolean>;
      permission: boolean;
      addEventListener(event: string, callback: (data: unknown) => void): void;
      removeEventListener(event: string, callback: (data: unknown) => void): void;
    };
  };
  plugins?: {
    OneSignal?: {
      initialize(appId: string): void;
      Notifications: {
        requestPermission(fallbackToSettings: boolean): Promise<boolean>;
        hasPermission(): boolean;
        addEventListener(event: string, callback: (data: unknown) => void): void;
        removeEventListener(event: string, callback: (data: unknown) => void): void;
      };
      User: {
        pushSubscription: {
          id: string | null;
          token: string | null;
          optedIn: boolean;
          addEventListener(event: string, callback: (data: unknown) => void): void;
        };
        addTag(key: string, value: string): void;
        addTags(tags: Record<string, string>): void;
        removeTag(key: string): void;
      };
      login(externalId: string): void;
      logout(): void;
    };
  };
  OneSignalDeferred?: any[];
}

declare const window: OneSignalWindow;

let isInitialized = false;

/**
 * Set OneSignal Player ID via Despia SDK
 * This connects the user ID stored in the database with the device registered in OneSignal
 */
export function setOneSignalPlayerId(userId: string): void {
  try {
    despia(`setonesignalplayerid://?user_id=${userId}`);
    addDiagnosticStep(`setonesignalplayerid (${userId.slice(0, 8)}...)`, true);
    diagnosticsState.lastUserId = userId;
  } catch (error) {
    addDiagnosticStep('setonesignalplayerid', false, String(error));
  }
}

/**
 * Request push permission via Despia
 */
function despiaRequestPermission(): void {
  try {
    despia("requestpushpermission://");
    addDiagnosticStep('requestpushpermission', true);
  } catch (error) {
    addDiagnosticStep('requestpushpermission', false, String(error));
  }
}

/**
 * Register push token via Despia
 */
function despiaRegisterPush(): void {
  try {
    despia("registerpush://");
    addDiagnosticStep('registerpush', true);
  } catch (error) {
    addDiagnosticStep('registerpush', false, String(error));
  }
}

/**
 * Initialize OneSignal SDK
 */
export async function initializePushNotifications(): Promise<void> {
  if (isInitialized) {
    console.log("[Push] Already initialized");
    return;
  }

  // Check if running in Capacitor/native environment
  const isNative = typeof window.plugins?.OneSignal !== "undefined";

  try {
    if (isNative) {
      const OneSignal = window.plugins!.OneSignal!;
      // Initialize with app ID
      OneSignal.initialize(ONESIGNAL_APP_ID);

      // Listen for notification clicks
      OneSignal.Notifications.addEventListener("click", (event) => {
        console.log("[Push] Notification clicked:", event);
        handleNotificationClick(event);
      });

      // Listen for foreground notifications
      OneSignal.Notifications.addEventListener("foregroundWillDisplay", (event) => {
        console.log("[Push] Foreground notification:", event);
      });

      // Listen for subscription changes
      OneSignal.User.pushSubscription.addEventListener("change", (event) => {
        console.log("[Push] Subscription changed:", event);
      });

      console.log("[Push] Native OneSignal initialized successfully");
    } else {
      // Web Initialization — skip if not in a Despia/native context, because the app
      // is NOT configured for web push (only native iOS/Android via Despia).
      // Initializing on plain browser would throw "App not configured for web push".
      const isDespia = navigator.userAgent.toLowerCase().includes('despia');
      if (!isDespia) {
        console.log("[Push] Skipping web OneSignal init: not a Despia/native context");
        isInitialized = true;
        return;
      }

      // Load SDK dynamically (not in index.html) to avoid
      // loading 44KB on desktop landing page where push notifications are not needed
      console.log("[Push] Loading Web OneSignal SDK dynamically...");
      await new Promise<void>((resolve, reject) => {
        if (document.querySelector('script[src*="OneSignalSDK"]')) {
          // Script already injected (e.g. by a previous call that was interrupted)
          resolve();
          return;
        }
        const script = document.createElement("script");
        script.src = "https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js";
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error("Failed to load OneSignal SDK"));
        document.head.appendChild(script);
      });
      window.OneSignalDeferred = window.OneSignalDeferred || [];
      window.OneSignalDeferred.push(async function (OneSignal: any) {
        await OneSignal.init({
          appId: ONESIGNAL_APP_ID,
        });
        console.log("[Push] Web OneSignal initialized");
      });
    }

    isInitialized = true;
  } catch (error) {
    console.error("[Push] Error initializing OneSignal:", error);
  }
}

/**
 * Request push notification permission
 */
export async function requestPushPermission(): Promise<boolean> {
  const isNative = typeof window.plugins?.OneSignal !== "undefined";

  try {
    let granted = false;

    if (isNative) {
      const OneSignal = window.plugins!.OneSignal!;
      granted = await OneSignal.Notifications.requestPermission(true);
    } else {
      // Web
      if (window.OneSignal) {
        granted = await window.OneSignal.Notifications.requestPermission();
      } else {
        console.warn("[Push] OneSignal Web SDK not loaded yet");
        return false;
      }
    }

    if (granted) {
      console.log("[Push] Permission granted");
      await linkDeviceToUser();
    } else {
      console.log("[Push] Permission denied");
    }

    return granted;
  } catch (error) {
    console.error("[Push] Error requesting permission:", error);
    return false;
  }
}

/**
 * Check if push notifications are enabled
 */
export function hasPushPermission(): boolean {
  const isNative = typeof window.plugins?.OneSignal !== "undefined";

  if (isNative) {
    return window.plugins!.OneSignal!.Notifications.hasPermission();
  } else {
    // Web
    return window.OneSignal?.Notifications?.permission ?? false;
  }
}

/**
 * Force registration of push notifications for Despia
 * This can be called manually from diagnostics UI
 */
export async function forceDespiaPushRegistration(): Promise<{ success: boolean; message: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      return { success: false, message: 'Nessun utente autenticato' };
    }

    diagnosticsState.registrationSteps = []; // Clear old steps
    addDiagnosticStep('Force registration started', true);

    // Execute the full Despia registration sequence with proper timing
    return new Promise((resolve) => {
      // Step 1: Set external_id first
      console.log("[Push] Force: Step 1 - Setting external_id first...");
      setOneSignalPlayerId(user.id);

      // Step 2: Request permission after brief delay
      setTimeout(() => {
        console.log("[Push] Force: Step 2 - Requesting push permission...");
        despiaRequestPermission();

        // Step 3: Register push after permission
        setTimeout(() => {
          console.log("[Push] Force: Step 3 - Registering push token...");
          despiaRegisterPush();

          // Step 4: Re-set external_id after registration
          setTimeout(() => {
            console.log("[Push] Force: Step 4 - Re-linking external_id...");
            setOneSignalPlayerId(user.id);

            addDiagnosticStep('Force registration completed', true);
            resolve({
              success: true,
              message: 'Registrazione completata. Controlla OneSignal tra 30 secondi.'
            });
          }, 1000);
        }, 1500);
      }, 800);
    });
  } catch (error) {
    addDiagnosticStep('Force registration failed', false, String(error));
    return { success: false, message: String(error) };
  }
}

/**
 * Send a test push notification to the current user
 */
export async function sendTestPushNotification(): Promise<{ success: boolean; message: string; data?: unknown }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      return { success: false, message: 'Nessun utente autenticato' };
    }

    const response = await supabase.functions.invoke('send-push-notification', {
      body: {
        user_id: user.id,
        title: '🔔 Test Notifica',
        message: `Push di test inviata alle ${new Date().toLocaleTimeString('it-IT')}`,
        data: { type: 'test' }
      }
    });

    if (response.error) {
      return {
        success: false,
        message: response.error.message,
        data: response.error
      };
    }

    return {
      success: true,
      message: `Notifica inviata! Recipients: ${response.data?.recipients || 'unknown'}`,
      data: response.data
    };
  } catch (error) {
    return { success: false, message: String(error) };
  }
}

/**
 * Send a local push notification via Despia (bypasses OneSignal)
 */
export function sendLocalTestPush(): void {
  try {
    const now = new Date().toLocaleTimeString('it-IT');
    despia(`sendlocalpushmsg://?title=Test Locale&message=Notifica locale inviata alle ${now}&deeplink=`);
    addDiagnosticStep('sendlocalpushmsg', true);
  } catch (error) {
    addDiagnosticStep('sendlocalpushmsg', false, String(error));
  }
}

/**
 * Link the device to the current authenticated user
 * Uses external_id approach - no database save needed
 * 
 * CRITICAL: Follows the Despia documentation sequence:
 * 1. setonesignalplayerid (set external_id first)
 * 2. requestpushpermission (show iOS popup)
 * 3. registerpush (register APNs token)
 * 4. setonesignalplayerid again (ensure link after registration)
 */
export async function linkDeviceToUser(): Promise<void> {
  // Debounce check
  const now = Date.now();
  if (isLinkingInProgress || (now - lastLinkAttempt) < LINK_DEBOUNCE_MS) {
    console.log("[Push] Link already in progress or too recent, skipping");
    return;
  }

  isLinkingInProgress = true;
  lastLinkAttempt = now;

  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      console.log("[Push] No user logged in, skipping device link");
      isLinkingInProgress = false;
      return;
    }

    // Enhanced environment detection
    const isDespia = navigator.userAgent.toLowerCase().includes('despia');
    const hasPlugins = typeof window.plugins !== 'undefined';
    const hasOneSignalPlugin = typeof window.plugins?.OneSignal !== 'undefined';

    // Update diagnostics state
    diagnosticsState = {
      ...diagnosticsState,
      userAgent: navigator.userAgent,
      isDespia,
      hasOneSignalPlugin,
      platform: navigator.platform,
      lastUserId: user.id,
    };

    console.log("[Push] Environment detection:", {
      userAgent: navigator.userAgent,
      isDespia,
      hasPlugins,
      hasOneSignalPlugin,
      platform: navigator.platform,
    });

    addDiagnosticStep('linkDeviceToUser started', true);

    // For Despia builds: Execute the CORRECT registration sequence
    // Based on Despia documentation: setonesignalplayerid FIRST, then registerpush
    if (isDespia) {
      console.log("[Push] Despia environment detected, starting push registration flow...");

      // Step 1: Set external_id FIRST (per Despia docs)
      console.log("[Push] Step 1: Setting external_id first...");
      setOneSignalPlayerId(user.id);

      // Step 2: Request push permission with delay
      setTimeout(() => {
        console.log("[Push] Step 2: Requesting push permission...");
        despiaRequestPermission();

        // Step 3: Register push token after permission
        setTimeout(() => {
          console.log("[Push] Step 3: Registering push token...");
          despiaRegisterPush();

          // Step 4: Re-link external_id after registration (ensures binding)
          setTimeout(() => {
            console.log("[Push] Step 4: Re-linking external_id after registration...");
            setOneSignalPlayerId(user.id);

            addDiagnosticStep('Registration sequence completed', true);
            isLinkingInProgress = false;
          }, 500);
        }, 1000);
      }, 800);

      return; // Exit early, async completion
    } else {
      // Non-Despia: Just set external_id
      console.log("[Push] Non-Despia environment, calling setOneSignalPlayerId directly");
      setOneSignalPlayerId(user.id);
    }

    // Handle native OneSignal SDK if available (Capacitor builds)
    const isNative = hasOneSignalPlugin;

    console.log("[Push] linkDeviceToUser completed", {
      userId: user.id,
      isNative,
      isDespia,
    });

    if (isNative) {
      const OneSignal = window.plugins!.OneSignal!;
      // Login to OneSignal with user ID as external ID
      OneSignal.login(user.id);

      // Add user tags for segmentation
      OneSignal.User.addTags({
        user_id: user.id,
        email: user.email || "",
      });
      console.log("[Push] Device linked to user via Native OneSignal SDK:", user.id);
    } else {
      // Web SDK - we should wait for init? Usually initialized by now if called in provider
      window.OneSignalDeferred = window.OneSignalDeferred || [];
      window.OneSignalDeferred.push(function (OneSignal: any) {
        OneSignal.login(user.id);
        // Web SDK doesn't always support user tags same way or might need User namespace
        // OneSignal.User.addTag("user_id", user.id);
        console.log("[Push] Device linked to user via Web OneSignal SDK:", user.id);
      });
    }

    addDiagnosticStep('OneSignal SDK login completed', true);
  } catch (error) {
    console.error("[Push] Error linking device to user:", error);
    addDiagnosticStep('linkDeviceToUser failed', false, String(error));
  }

  isLinkingInProgress = false;
}

/**
 * Unlink the device from the current user (on logout)
 */
export function unlinkDeviceFromUser(): void {
  const isNative = typeof window.plugins?.OneSignal !== "undefined";

  if (!isNative) {
    return;
  }

  try {
    window.plugins!.OneSignal!.logout();
    console.log("[Push] Device unlinked from user");
  } catch (error) {
    console.error("[Push] Error unlinking device:", error);
  }
}

/**
 * Handle notification click events
 */
function handleNotificationClick(event: unknown): void {
  const notification = event as {
    notification?: {
      additionalData?: {
        type?: string;
        url?: string;
        redemption_id?: string;
        promo_id?: string;
      };
    };
  };

  const data = notification?.notification?.additionalData;

  if (!data) {
    return;
  }

  // Navigate based on notification type
  if (data.url) {
    window.location.href = data.url;
  } else if (data.type === "redemption_update" && data.redemption_id) {
    window.location.href = `/miei-riscatti`;
  } else if (data.type === "promo" && data.promo_id) {
    window.location.href = `/volantino`;
  } else if (data.type === "points") {
    window.location.href = `/punti`;
  }
}

/**
 * Check if running in native environment
 */
export function isNativeEnvironment(): boolean {
  return typeof window.plugins?.OneSignal !== "undefined";
}
